import type { ReactNode } from 'react';
import type { DeploymentRecord } from '@/services/deploymentService';
import { ACCESS_NOTE, SLOT_HINT, STUCK_HINT, retryLine, useRunProgressView } from '@/hooks/useRunProgressView';
import type { ProgressSnapshot } from '@/utils/deployProgressPoller';
import { PHASE_TRAIL, formatCount, formatPlan, shortenResourceAddress } from '@/utils/deployProgress';
import { DeployProgressBar } from './DeployProgressBar';
import './deployments.css';

interface RunProgressProps {
  record: DeploymentRecord;
  /** The run's progress from the shared poller. Absent while the run has no task yet. */
  snapshot?: ProgressSnapshot;
}

/**
 * A live run's status line, on the run's own page: the phase, a bar, how many
 * resources are done, and how long the phase and the whole run have taken. It
 * reads the same as the terminal status line for a local deploy. In a table,
 * a run's one-line form is `RowProgress`.
 */
export function RunProgress({ record, snapshot }: RunProgressProps) {
  const view = useRunProgressView(record, snapshot);
  const { label, hint, phase, progress, percent, hasCount, showBar, stuck, waiting, reading, retryIn } = view;
  const barLabel = `${record.clientName} deploy progress`;

  // Between two attempts there is nothing to measure: say when the second one starts.
  if (retryIn !== null) {
    const why = record.errorTitle || record.firstAttemptErrorSummary;
    return (
      <div className="deploy-progress">
        <div className="deploy-progress__line">
          <span className="deploy-progress__phase deploy-progress__phase--retry" title={hint}>
            {label}
          </span>
          <span className="deploy-progress__facts numeric">
            <span>{retryLine(retryIn)}</span>
            <span>total {view.total}</span>
          </span>
        </div>
        {why && <div className="deploy-progress__quiet">The first attempt failed: {why}</div>}
      </div>
    );
  }

  let quiet: ReactNode = null;
  if (stuck) {
    quiet = STUCK_HINT;
  } else if (waiting) {
    quiet = SLOT_HINT;
  } else if (progress?.plan && hasCount) {
    quiet = (
      <>
        {formatPlan(progress.plan)}
        {progress.lastResource && phase !== 'done' && (
          <>
            {' · last: '}
            <span className="mono" title={progress.lastResource}>
              {shortenResourceAddress(progress.lastResource, record.clientName)}
            </span>
          </>
        )}
        {phase === 'done' && ' · Finishing up.'}
      </>
    );
  } else if (progress?.noChanges) {
    quiet = 'The plan found nothing to change.';
  } else if (!reading) {
    const step = PHASE_TRAIL.indexOf(phase);
    quiet = step >= 0 ? `Step ${step + 1} of ${PHASE_TRAIL.length}. ${hint}` : hint;
  }

  return (
    <div className="deploy-progress">
      <div className="deploy-progress__line">
        <span className={`deploy-progress__phase${stuck ? ' deploy-progress__phase--stuck' : ''}`} title={hint}>
          {label}
        </span>
        {showBar && <DeployProgressBar percent={percent} label={barLabel} />}
        {percent !== null && <span className="deploy-progress__percent numeric">{percent}%</span>}
        <span className="deploy-progress__facts numeric">
          {hasCount && progress && (
            <span>
              {formatCount(progress.done)} of {formatCount(progress.total)} resources
            </span>
          )}
          {progress && progress.errors > 0 && (
            <span className="deploy-progress__errors" title={progress.lastError}>
              {progress.errors} {progress.errors === 1 ? 'error' : 'errors'}
            </span>
          )}
          <span>{view.phaseTime ? `${view.phaseTime} · total ${view.total}` : `total ${view.total}`}</span>
        </span>
      </div>
      {quiet && <div className="deploy-progress__quiet">{quiet}</div>}
      {view.nearAccessLimit && <div className="deploy-progress__warn">{ACCESS_NOTE}</div>}
    </div>
  );
}

export default RunProgress;
